import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';
import nodeLibraryService from '../../../services/nodeLibraryService';
import nodeCategoryService from '../../../services/nodeCategoryService';
import nodeLibraryRealtimeService from '../../../services/nodeLibraryRealtimeService';
import nodeTypeService from '../../../services/nodeTypeService';

// Modal component defined OUTSIDE to prevent re-creation on parent re-renders
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-xl font-semibold text-foreground">{title}</h3>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const NodeLibraryManagementPanel = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedNodeType, setSelectedNodeType] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isCategoryManagementModalOpen, setIsCategoryManagementModalOpen] = useState(false);
  const [isNodeTypeModalOpen, setIsNodeTypeModalOpen] = useState(false);
  const [isNodeTypeManagementModalOpen, setIsNodeTypeManagementModalOpen] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'tasks',
    nodeType: 'task',
    icon: 'Square',
    description: '',
    color: '#3B82F6',
    shortcut: ''
  });
  const [categoryFormData, setCategoryFormData] = useState({
    categoryValue: '',
    categoryLabel: ''
  });
  const [nodeTypeFormData, setNodeTypeFormData] = useState({
    typeValue: '',
    typeLabel: ''
  });

  const [nodeLibrary, setNodeLibrary] = useState([]);
  const [categories, setCategories] = useState([
    { value: 'all', label: 'All Categories' }
  ]);
  const [nodeTypes, setNodeTypes] = useState([
    { value: 'all', label: 'All Node Types' }
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryDeleting, setCategoryDeleting] = useState(false);
  const [deletingAllNodes, setDeletingAllNodes] = useState(false);
  const [nodeTypeSaving, setNodeTypeSaving] = useState(false);
  const [nodeTypeDeleting, setNodeTypeDeleting] = useState(false);

  // Track if any modal is open to prevent real-time updates during editing
  const isAnyModalOpenRef = useRef(false);

  // Add this block - Define modal control functions
  const openAddModal = () => {
    isAnyModalOpenRef.current = true;
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    isAnyModalOpenRef.current = false;
    setIsAddModalOpen(false);
    resetForm();
  };

  const openEditModal = (node) => {
    isAnyModalOpenRef.current = true;
    setEditingNode(node);
    setFormData({
      name: node?.name || '',
      category: node?.category || 'tasks',
      nodeType: node?.node_type || 'task',
      icon: node?.icon || 'Square',
      description: node?.description || '',
      color: node?.color || '#3B82F6',
      shortcut: node?.shortcut || ''
    });
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    isAnyModalOpenRef.current = false;
    setIsEditModalOpen(false);
    setEditingNode(null);
    resetForm();
  };

  const openCategoryModal = () => {
    isAnyModalOpenRef.current = true;
    setIsCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    isAnyModalOpenRef.current = false;
    setIsCategoryModalOpen(false);
    setCategoryFormData({ categoryValue: '', categoryLabel: '' });
  };

  const openCategoryManagementModal = () => {
    isAnyModalOpenRef.current = true;
    setIsCategoryManagementModalOpen(true);
  };

  const closeCategoryManagementModal = () => {
    isAnyModalOpenRef.current = false;
    setIsCategoryManagementModalOpen(false);
  };

  const openNodeTypeModal = () => {
    isAnyModalOpenRef.current = true;
    setIsNodeTypeModalOpen(true);
  };

  const closeNodeTypeModal = () => {
    isAnyModalOpenRef.current = false;
    setIsNodeTypeModalOpen(false);
    setNodeTypeFormData({ typeValue: '', typeLabel: '' });
  };

  const openNodeTypeManagementModal = () => {
    isAnyModalOpenRef.current = true;
    setIsNodeTypeManagementModalOpen(true);
  };

  const closeNodeTypeManagementModal = () => {
    isAnyModalOpenRef.current = false;
    setIsNodeTypeManagementModalOpen(false);
  };
  // End of added block

  // Wrapper functions to update ref synchronously before state changes
  // Update ref whenever modal states change

  const iconOptions = [
    'Square', 'Layers', 'ClipboardCheck', 'Wrench', 'User', 'Cpu', 'Package',
    'GitBranch', 'GitMerge', 'Clock', 'ArrowRight', 'ArrowDownRight', 'Circle',
    'CheckCircle', 'AlertCircle', 'Settings', 'Database', 'FileText'
  ];

  const filteredNodes = nodeLibrary?.filter(node => {
    const matchesSearch = node?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         node?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || node?.category === selectedCategory;
    const matchesNodeType = selectedNodeType === 'all' || node?.node_type === selectedNodeType;
    return matchesSearch && matchesCategory && matchesNodeType;
  });

  useEffect(() => {
    loadNodes();
    loadCategories();
    loadNodeTypes();

    // Subscribe to real-time node changes
    const nodeChannel = nodeLibraryRealtimeService?.subscribeToNodeChanges((payload) => {
      console.log('Node library change detected in management panel:', payload?.eventType);
      // Only reload if no modal is open (prevents input focus loss)
      if (!isAnyModalOpenRef?.current) {
        loadNodes();
      }
    });

    // Subscribe to category changes
    const categoryChannel = nodeLibraryRealtimeService?.subscribeToNodeCategoryChanges((payload) => {
      console.log('Node category change detected in management panel:', payload?.eventType);
      // Only reload if no modal is open (prevents input focus loss)
      if (!isAnyModalOpenRef?.current) {
        loadCategories();
      }
    });

    // Cleanup subscriptions on unmount
    return () => {
      nodeLibraryRealtimeService?.unsubscribeAll([nodeChannel, categoryChannel]);
    };
  }, []);

  const loadCategories = async () => {
    try {
      const fetchedCategories = await nodeCategoryService?.getAllCategories();
      const formattedCategories = [
        { value: 'all', label: 'All Categories' },
        ...fetchedCategories?.map(cat => ({
          id: cat?.id,
          value: cat?.categoryValue,
          label: cat?.categoryLabel,
          isSystem: cat?.isSystem
        }))
      ];
      setCategories(formattedCategories);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const loadNodeTypes = async () => {
    try {
      const fetchedNodeTypes = await nodeTypeService?.getAllNodeTypes();
      const formattedNodeTypes = [
        { value: 'all', label: 'All Node Types' },
        ...fetchedNodeTypes?.map(type => ({
          id: type?.id,
          value: type?.type_value,
          label: type?.type_label,
          isSystem: type?.is_system
        }))
      ];
      setNodeTypes(formattedNodeTypes);
    } catch (err) {
      console.error('Error loading node types:', err);
    }
  };

  const loadNodes = async () => {
    try {
      setLoading(true);
      setError('');
      const nodes = await nodeLibraryService?.getAllNodes();
      setNodeLibrary(nodes);
    } catch (err) {
      setError(err?.message || 'Failed to load nodes');
      console.error('Error loading nodes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    try {
      setCategorySaving(true);
      setError('');
      
      // Validate input
      if (!categoryFormData?.categoryLabel?.trim()) {
        setError('Category name is required');
        return;
      }

      // Generate category value from label (lowercase, replace spaces with underscores)
      const categoryValue = categoryFormData?.categoryLabel
        ?.toLowerCase()
        ?.replace(/\s+/g, '_')
        ?.replace(/[^a-z0-9_]/g, '');

      await nodeCategoryService?.createCategory({
        categoryValue,
        categoryLabel: categoryFormData?.categoryLabel?.trim()
      });

      // Reload categories
      await loadCategories();
      
      closeCategoryModal();
    } catch (err) {
      setError(err?.message || 'Failed to add category');
      console.error('Error adding category:', err);
    } finally {
      setCategorySaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      setCategoryDeleting(true);
      setError('');

      // Get category details to check if it's a system category
      const category = categories?.find(c => c?.id === categoryId);
      
      if (category?.isSystem) {
        setError('System categories cannot be deleted');
        setCategoryDeleting(false);
        return;
      }

      if (window.confirm(`Are you sure you want to delete the category "${category?.label}"?\n\nNote: Nodes in this category will not be deleted, but you may need to reassign them.`)) {
        await nodeCategoryService?.deleteCategory(categoryId);
        await loadCategories();
        
        // Reset selected category if the deleted one was selected
        if (selectedCategory === category?.value) {
          setSelectedCategory('all');
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to delete category');
      console.error('Error deleting category:', err);
    } finally {
      setCategoryDeleting(false);
    }
  };

  const handleDeleteAllCustomCategories = async () => {
    const customCategories = categories?.filter(c => c?.value !== 'all' && !c?.isSystem);
    
    if (customCategories?.length === 0) {
      alert('No custom categories to delete');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ALL ${customCategories?.length} custom categories?\n\nThis action cannot be undone.\n\nNote: Nodes in these categories will not be deleted, but you may need to reassign them.`)) {
      try {
        setCategoryDeleting(true);
        setError('');
        await nodeCategoryService?.deleteAllCustomCategories();
        await loadCategories();
        setSelectedCategory('all');
        setIsCategoryManagementModalOpen(false);
      } catch (err) {
        setError(err?.message || 'Failed to delete categories');
        console.error('Error deleting categories:', err);
      } finally {
        setCategoryDeleting(false);
      }
    }
  };

  const handleAddNode = async () => {
    try {
      setSaving(true);
      setError('');

      // Validate required fields
      if (!formData?.name?.trim()) {
        setError('Node name is required');
        setSaving(false);
        return;
      }
      if (!formData?.category) {
        setError('Category is required');
        setSaving(false);
        return;
      }

      const newNode = await nodeLibraryService?.createNode({
        name: formData?.name,
        category: formData?.category,
        categoryName: categories?.find(c => c?.value === formData?.category)?.label || 'Custom',
        nodeType: formData?.nodeType,
        icon: formData?.icon,
        description: formData?.description,
        color: formData?.color,
        shortcut: formData?.shortcut
      });
      setNodeLibrary([...nodeLibrary, newNode]);
      setIsAddModalOpen(false);
      resetForm();
      // Reload data after modal closes to catch any missed updates
      await loadNodes();
    } catch (err) {
      setError(err?.message || 'Failed to add node');
      console.error('Error adding node:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditNode = async () => {
    try {
      setSaving(true);
      setError('');
      const updatedNode = await nodeLibraryService?.updateNode(editingNode?.id, {
        name: formData?.name,
        category: formData?.category,
        categoryName: categories?.find(c => c?.value === formData?.category)?.label || 'Custom',
        nodeType: formData?.nodeType,
        icon: formData?.icon,
        description: formData?.description,
        color: formData?.color,
        shortcut: formData?.shortcut
      });
      setNodeLibrary(nodeLibrary?.map(node =>
        node?.id === editingNode?.id ? updatedNode : node
      ));
      setIsEditModalOpen(false);
      setEditingNode(null);
      resetForm();
      // Reload data after modal closes to catch any missed updates
      await loadNodes();
    } catch (err) {
      setError(err?.message || 'Failed to update node');
      console.error('Error updating node:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNode = async (nodeId) => {
    const node = nodeLibrary?.find(n => n?.id === nodeId);

    // Removed system node check - allow deletion of all nodes
    const confirmMessage = node?.isSystem 
      ? `Are you sure you want to delete the system node "${node?.name}"? This action cannot be undone.`
      : `Are you sure you want to delete "${node?.name}"? This action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Remove duplicate confirmation - already confirmed above
    try {
      setError('');
      await nodeLibraryService?.deleteNode(nodeId);
      setNodeLibrary(nodeLibrary?.filter(node => node?.id !== nodeId));
    } catch (err) {
      setError(err?.message || 'Failed to delete node');
      console.error('Error deleting node:', err);
    }
  };

  const handleDeleteAllNodes = async () => {
    const customNodes = nodeLibrary?.filter(node => !node?.isSystem);
    
    if (customNodes?.length === 0) {
      alert('No custom nodes to delete');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ALL ${customNodes?.length} custom nodes?\n\nThis action cannot be undone.\n\nSystem nodes will not be affected.`)) {
      try {
        setDeletingAllNodes(true);
        setError('');
        await nodeLibraryService?.deleteAllNodes();
        // Reload nodes to get fresh data (will only show system nodes)
        await loadNodes();
      } catch (err) {
        setError(err?.message || 'Failed to delete all nodes');
        console.error('Error deleting all nodes:', err);
      } finally {
        setDeletingAllNodes(false);
      }
    }
  };

  const handleAddNodeType = async () => {
    try {
      setNodeTypeSaving(true);
      setError('');

      // Validate input
      if (!nodeTypeFormData?.typeLabel?.trim()) {
        setError('Node type name is required');
        return;
      }

      // Generate type value from label (lowercase, replace spaces with underscores)
      const typeValue = nodeTypeFormData?.typeLabel
        ?.toLowerCase()
        ?.replace(/\s+/g, '_')
        ?.replace(/[^a-z0-9_]/g, '');

      await nodeTypeService?.createNodeType({
        typeValue,
        typeLabel: nodeTypeFormData?.typeLabel?.trim(),
        userId: user?.id
      });

      // Reload node types
      await loadNodeTypes();
      
      closeNodeTypeModal();
    } catch (err) {
      setError(err?.message || 'Failed to add node type');
      console.error('Error adding node type:', err);
    } finally {
      setNodeTypeSaving(false);
    }
  };

  const handleDeleteNodeType = async (nodeTypeId) => {
    try {
      setNodeTypeDeleting(true);
      setError('');

      // Get node type details
      const nodeType = nodeTypes?.find(nt => nt?.id === nodeTypeId);

      const confirmMessage = nodeType?.isSystem
        ? `Are you sure you want to delete the system node type "${nodeType?.label}"? This action cannot be undone.\n\nNote: Nodes with this type will not be deleted, but you may need to reassign them.`
        : `Are you sure you want to delete the node type "${nodeType?.label}"?\n\nNote: Nodes with this type will not be deleted, but you may need to reassign them.`;

      if (window.confirm(confirmMessage)) {
        await nodeTypeService?.deleteNodeType(nodeTypeId);
        await loadNodeTypes();
      }
    } catch (err) {
      setError(err?.message || 'Failed to delete node type');
      console.error('Error deleting node type:', err);
    } finally {
      setNodeTypeDeleting(false);
    }
  };

  const handleDeleteAllNodeTypes = async () => {
    const allNodeTypes = nodeTypes?.filter(nt => nt?.value !== 'all');

    if (allNodeTypes?.length === 0) {
      alert('No node types to delete');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ALL ${allNodeTypes?.length} node types (including system types)?\n\nThis action cannot be undone.\n\nNote: Nodes with these types will not be deleted, but you may need to reassign them.`;

    if (window.confirm(confirmMessage)) {
      if (!user?.id) {
        setError('You must be logged in to delete node types');
        return;
      }

      try {
        setNodeTypeDeleting(true);
        setError('');
        const result = await nodeTypeService?.deleteAllCustomNodeTypes(user?.id);
        await loadNodeTypes();
        setSelectedNodeType('all');
        
        // Show success message with count
        if (result?.deletedCount > 0) {
          alert(`Successfully deleted ${result?.deletedCount} custom node type${result?.deletedCount !== 1 ? 's' : ''}`);
          setIsNodeTypeManagementModalOpen(false);
        } else {
          setError('No node types were deleted. You can only delete node types that you created.');
        }
      } catch (err) {
        setError(err?.message || 'Failed to delete node types');
        console.error('Error deleting node types:', err);
      } finally {
        setNodeTypeDeleting(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'tasks',
      nodeType: 'task',
      icon: 'Square',
      description: '',
      color: '#3B82F6',
      shortcut: ''
    });
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg flex items-center gap-2">
          <Icon name="AlertCircle" size={20} />
          <span className="text-sm">{error}</span>
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Node Library Management</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage workflow nodes available in the visual designer
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleDeleteAllNodes}
            variant="outline"
            className="flex items-center gap-2 text-destructive hover:bg-destructive/10"
            disabled={deletingAllNodes || nodeLibrary?.length === 0}
            loading={deletingAllNodes}
          >
            <Icon name="Trash2" size={18} />
            Delete All Nodes
          </Button>
          <Button
            onClick={openAddModal}
            className="flex items-center gap-2"
          >
            <Icon name="Plus" size={18} />
            Add New Node
          </Button>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search nodes by name or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full"
          />
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <div className="flex-1 md:w-64">
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e?.target?.value)}
              options={categories}
            />
          </div>
          <Button
            onClick={() => setIsCategoryModalOpen(true)}
            variant="outline"
            className="flex items-center justify-center w-10 h-10 px-0"
            title="Add new category"
          >
            <Icon name="Plus" size={18} />
          </Button>
          <Button
            onClick={() => setIsCategoryManagementModalOpen(true)}
            variant="outline"
            className="flex items-center justify-center w-10 h-10 px-0"
            title="Manage categories"
          >
            <Icon name="Settings" size={18} />
          </Button>
          <div className="flex-1 md:w-64">
            <Select
              value={selectedNodeType}
              onChange={(e) => setSelectedNodeType(e?.target?.value)}
              options={nodeTypes}
            />
          </div>
          <Button
            onClick={() => setIsNodeTypeModalOpen(true)}
            variant="outline"
            className="flex items-center justify-center w-10 h-10 px-0"
            title="Add new node type"
          >
            <Icon name="Plus" size={18} />
          </Button>
          <Button
            onClick={() => setIsNodeTypeManagementModalOpen(true)}
            variant="outline"
            className="flex items-center justify-center w-10 h-10 px-0"
            title="Manage node types"
          >
            <Icon name="Settings" size={18} />
          </Button>
        </div>
      </div>
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader" size={48} color="var(--color-primary)" className="animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Node</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Node Type</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Icon</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Description</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Shortcut</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredNodes?.map((node) => (
                  <tr key={node?.id} className="hover:bg-muted/50 transition-colors duration-200">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="flex items-center justify-center w-10 h-10 rounded-lg"
                          style={{ backgroundColor: `${node?.color}20`, color: node?.color }}
                        >
                          <Icon name={node?.icon} size={20} />
                        </div>
                        <span className="text-sm font-medium text-foreground">{node?.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {node?.categoryName || node?.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {nodeTypes?.find(nt => nt?.value === node?.node_type)?.label || node?.node_type || 'Task'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted/10">
                        <Icon name={node?.icon} size={20} />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-muted-foreground">{node?.description}</span>
                    </td>
                    <td className="px-6 py-4">
                      {node?.shortcut && (
                        <span className="px-2 py-1 text-xs font-medium rounded bg-muted text-foreground">
                          {node?.shortcut}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            openEditModal(node);
                          }}
                          className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors duration-200 hover:bg-muted`}
                          title="Edit node"
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            handleDeleteNode(node?.id);
                          }}
                          className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors duration-200 hover:bg-destructive/10 hover:text-destructive`}
                          title="Delete node"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredNodes?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <Icon name="Search" size={48} color="var(--color-muted-foreground)" />
            <p className="mt-4 text-sm text-muted-foreground">No nodes found</p>
          </div>
        )}
      </div>
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" />
          <div>
            <h4 className="text-sm font-medium text-foreground">About Node Library</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Changes made here will be reflected in the Visual Workflow Designer&apos;s node library panel.
              Users can drag these nodes onto the canvas to build workflows.
            </p>
          </div>
        </div>
      </div>
      {/* Add Category Modal */}
      <Modal
        isOpen={isCategoryModalOpen}
        onClose={closeCategoryModal}
        title="Add New Category"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Category Name</label>
            <Input
              type="text"
              placeholder="Enter category name (e.g., Data Processing)"
              value={categoryFormData?.categoryLabel}
              onChange={(e) => setCategoryFormData({ ...categoryFormData, categoryLabel: e?.target?.value })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              This will be displayed in the category dropdown
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={closeCategoryModal}
              disabled={categorySaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddCategory}
              disabled={!categoryFormData?.categoryLabel?.trim() || categorySaving}
              loading={categorySaving}
            >
              Add Category
            </Button>
          </div>
        </div>
      </Modal>
      {/* Category Management Modal */}
      <Modal
        isOpen={isCategoryManagementModalOpen}
        onClose={closeCategoryManagementModal}
        title="Manage Categories"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-4 border-b border-border">
            <div>
              <h4 className="text-sm font-medium text-foreground">All Categories</h4>
              <p className="text-xs text-muted-foreground mt-1">
                System categories cannot be deleted
              </p>
            </div>
            <Button
              onClick={handleDeleteAllCustomCategories}
              variant="outline"
              className="text-destructive hover:bg-destructive/10"
              disabled={categoryDeleting || categories?.filter(c => c?.value !== 'all' && !c?.isSystem)?.length === 0}
            >
              <Icon name="Trash2" size={16} className="mr-2" />
              Delete All Custom
            </Button>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {categories
              ?.filter(c => c?.value !== 'all')
              ?.map((category) => (
                <div
                  key={category?.value}
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors duration-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                      <Icon name="Folder" size={16} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{category?.label}</p>
                      <p className="text-xs text-muted-foreground">{category?.value}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {category?.isSystem ? (
                      <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700">
                        System
                      </span>
                    ) : (
                      <button
                        onClick={() => handleDeleteCategory(category?.id)}
                        disabled={categoryDeleting}
                        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors duration-200"
                        title="Delete category"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>

          {categories?.filter(c => c?.value !== 'all')?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8">
              <Icon name="Folder" size={48} color="var(--color-muted-foreground)" />
              <p className="mt-4 text-sm text-muted-foreground">No categories found</p>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={closeCategoryManagementModal}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
      {/* Add Node Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        title="Add New Node"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Node Name</label>
            <Input
              type="text"
              placeholder="Enter node name"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Category</label>
            <Select
              value={formData?.category}
              onChange={(value) => setFormData({ ...formData, category: value })}
              options={categories?.filter(c => c?.value !== 'all')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Node Type</label>
            <Select
              value={formData?.nodeType}
              onChange={(value) => setFormData({ ...formData, nodeType: value })}
              options={nodeTypes?.filter(nt => nt?.value !== 'all')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Icon</label>
            <Select
              value={formData?.icon}
              onChange={(value) => setFormData({ ...formData, icon: value })}
              options={iconOptions?.map(icon => ({ value: icon, label: icon }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description</label>
            <Input
              type="text"
              placeholder="Enter node description"
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Color</label>
            <input
              type="color"
              value={formData?.color}
              onChange={(e) => setFormData({ ...formData, color: e?.target?.value })}
              className="w-full h-10 rounded-lg border border-border cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Keyboard Shortcut</label>
            <Input
              type="text"
              placeholder="e.g., T"
              maxLength={1}
              value={formData?.shortcut}
              onChange={(e) => setFormData({ ...formData, shortcut: e?.target?.value?.toUpperCase() })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={closeAddModal}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddNode}
              disabled={!formData?.name || !formData?.category || saving}
              loading={saving}
            >
              Add Node
            </Button>
          </div>
        </div>
      </Modal>
      {/* Edit Node Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        title="Edit Node"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Node Name</label>
            <Input
              type="text"
              placeholder="Enter node name"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Category</label>
            <Select
              value={formData?.category}
              onChange={(value) => setFormData({ ...formData, category: value })}
              options={categories?.filter(c => c?.value !== 'all')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Node Type</label>
            <Select
              value={formData?.nodeType}
              onChange={(value) => setFormData({ ...formData, nodeType: value })}
              options={nodeTypes?.filter(nt => nt?.value !== 'all')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Icon</label>
            <Select
              value={formData?.icon}
              onChange={(value) => setFormData({ ...formData, icon: value })}
              options={iconOptions?.map(icon => ({ value: icon, label: icon }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description</label>
            <Input
              type="text"
              placeholder="Enter node description"
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Color</label>
            <input
              type="color"
              value={formData?.color}
              onChange={(e) => setFormData({ ...formData, color: e?.target?.value })}
              className="w-full h-10 rounded-lg border border-border cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Keyboard Shortcut</label>
            <Input
              type="text"
              placeholder="e.g., T"
              maxLength={1}
              value={formData?.shortcut}
              onChange={(e) => setFormData({ ...formData, shortcut: e?.target?.value?.toUpperCase() })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={closeEditModal}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditNode}
              disabled={!formData?.name || !formData?.category || saving}
              loading={saving}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>
      {/* Add Node Type Modal */}
      <Modal
        isOpen={isNodeTypeModalOpen}
        onClose={closeNodeTypeModal}
        title="Add New Node Type"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Node Type Name</label>
            <Input
              type="text"
              placeholder="Enter node type name (e.g., Approval, Review)"
              value={nodeTypeFormData?.typeLabel}
              onChange={(e) => setNodeTypeFormData({ ...nodeTypeFormData, typeLabel: e?.target?.value })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              This will be displayed in the node type dropdown
            </p>
          </div>

          {error && (
            <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={closeNodeTypeModal}
              disabled={nodeTypeSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddNodeType}
              disabled={!nodeTypeFormData?.typeLabel?.trim() || nodeTypeSaving}
              loading={nodeTypeSaving}
            >
              Add Node Type
            </Button>
          </div>
        </div>
      </Modal>
      {/* Node Type Management Modal */}
      <Modal
        isOpen={isNodeTypeManagementModalOpen}
        onClose={closeNodeTypeManagementModal}
        title="Manage Node Types"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-4 border-b border-border">
            <div>
              <h4 className="text-sm font-medium text-foreground">All Node Types</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Manage all node types including system types
              </p>
            </div>
            <Button
              onClick={handleDeleteAllNodeTypes}
              variant="outline"
              className="text-destructive hover:bg-destructive/10"
              disabled={nodeTypeDeleting || nodeTypes?.filter(nt => nt?.value !== 'all')?.length === 0}
            >
              <Icon name="Trash2" size={16} className="mr-2" />
              Delete All
            </Button>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {nodeTypes
              ?.filter(nt => nt?.value !== 'all')
              ?.map((nodeType) => (
                <div
                  key={nodeType?.value}
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors duration-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                      <Icon name="Box" size={16} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{nodeType?.label}</p>
                      <p className="text-xs text-muted-foreground">{nodeType?.value}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {nodeType?.isSystem && (
                      <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700">
                        System
                      </span>
                    )}
                    <button
                      onClick={() => handleDeleteNodeType(nodeType?.id)}
                      disabled={nodeTypeDeleting}
                      className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors duration-200"
                      title="Delete node type"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </div>
              ))}
          </div>

          {nodeTypes?.filter(nt => nt?.value !== 'all')?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8">
              <Icon name="Box" size={48} color="var(--color-muted-foreground)" />
              <p className="mt-4 text-sm text-muted-foreground">No node types found</p>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={closeNodeTypeManagementModal}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default NodeLibraryManagementPanel;